import React, { useState, useEffect, useContext } from "react";
import {
    Stack,
    GTIWebTextField, GTIWebButton, GTIWebDatePicker, GTIWebSelect
} from "@gti-controls-v2/web-core";
import Divider from "../divider";
import homePageStyles from "../../../common/styles/HomePage";
import useLookup from "../../../hooks/useLookups";
import { useNavigate, useLocation } from "react-router-dom";
import { paths } from "../../../common/constants/Rutas";
import { SearchContext } from "../../../providers/search/index";

const Search = () => {
    const location = useLocation();
    const styles = homePageStyles;
    const navigate = useNavigate();
    const { getStatesByCountry } = useLookup();
    const { state, dispatch } = useContext(SearchContext);

    const initSearchState = {
        byForm: {
            nombre: "",
            apellido: "",
            fechaNacimiento: null,
            estadoNacimiento: 0
        },
        byCURP: {
            curp: ""
        }
    };

    const [search, setSearch] = useState({ ...initSearchState });
    const [states, setStates] = useState(null);
    const [searchType, setSearchType] = useState(state.typeSearch || null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (state.typeSearch) {
            setSearch({
                byCURP: state.byCURP,
                byForm: state.byForm
            });
            setSearchType(state.typeSearch);
        }
    }, [state]);

    useEffect(() => {
        getStatesByCountryHandler();
        // eslint-disable-next-line
    }, []);

    const getStatesByCountryHandler = async () => {
        const result = await getStatesByCountry(1);
        setStates(result.data);
    };

    const handlerOnChange = (e) => {
        const { name, value } = e.target;
        
        if (name === "curp") {
            setSearch({
                byCURP: { curp: value },
                byForm: initSearchState.byForm,
            });
            setSearchType(value ? "CURP" : null);
        } else {
            if (state.byCURP.curp !== "") {
                setSearch((prev) => ({
                    ...prev,
                    byForm: {
                        ...prev.byForm,
                        [name]: value,
                    },
                    byCURP: { curp: state.byCURP.curp },
                }));
                setSearchType(state.typeSearch);
            } else {
                setSearch((prev) => ({
                    ...prev,
                    byForm: {
                        ...prev.byForm,
                        [name]: value,
                    },
                    byCURP: { curp: "" },
                }));
                if ((name === "nombre" || name === "apellido") && searchType !== 'FORM') setSearchType(value ? "FORM" : null);
            }
        }
    };

    const handlerOnClick = (e) => {
        const { name } = e.target;
        const actions = {
            search: handleSearch,
            curp: handleSearch,
            nombre: handleSearch,
            apellido: handleSearch,
            clear: handleClear,
        };

        if (actions[name]) {
            actions[name]();
        }
    };

    const handleSearch = () => {
        if (!validateSearch()) return;

        let searchParams =
            searchType === "CURP"
                ? {
                    typeSearch: "CURP",
                    byCURP: search.byCURP,
                    byForm: initSearchState.byForm,
                }
                : {
                    typeSearch: "FORM",
                    byCURP: initSearchState.byCURP,
                    byForm: search.byForm,
                };

        if (searchType === "FORM") {
            const today = new Date();
            const isTodaySpecial = today.toISOString().split("T")[0] === searchParams.byForm.fechaNacimiento;
            searchParams.byForm.fechaNacimiento = isTodaySpecial ? "" : searchParams.byForm.fechaNacimiento;
            searchParams.byForm.fechaNacimiento = searchParams.byForm.fechaNacimiento === null ? "" : searchParams.byForm.fechaNacimiento;
        }

        dispatch({ type: "SET_SEARCH", payload: searchParams });

        if (location !== paths.ResultSearchPage) {
            navigate(paths.ResultSearchPage)
        }
    };

    const handleClear = () => {
        setSearch(initSearchState);
        setSearchType(null);
        setErrors({});
        dispatch({ type: "CLEAR_SEARCH" });
    };

    const validateSearch = () => {
        const newErrors = {};
        if (search.byCURP.curp) {
            if (search.byCURP.curp.length < 4) {
                newErrors.curp = "El CURP debe tener al menos 4 carácteres";
            }
        } else {
            if (!search.byForm.nombre && !search.byForm.apellido) {
                newErrors.nombre = "El nombre es obligatorio si no se usa el CURP.";
            }
            if (!search.byForm.apellido && !search.byForm.nombre) {
                newErrors.apellido = "El apellido es obligatorio si no se usa el CURP.";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isSearchDisabled = () => {
        const { byForm, byCURP } = search;
        return !(
            byCURP.curp ||
            byForm.nombre ||
            byForm.apellido
        );
    };

    const isCURPFieldDisabled = searchType === "FORM";
    const areFormFieldsDisabled = searchType === "CURP";

    return (
        <Stack sx={styles.searchContainer} spacing={2}>
            <GTIWebTextField
                name="curp"
                labelText="CURP"
                placeholder="Ingresa el CURP del empleado"
                width="100%"
                onBlur={handlerOnChange}
                onChange={handlerOnChange}
                onEnter={handlerOnClick}
                value={search.byCURP.curp}
                disabled={isCURPFieldDisabled}
                error={errors.curp}
            />

            <Divider />

            <GTIWebTextField
                name="nombre"
                labelText="Nombre"
                placeholder="Nombre o nombres del empleado"
                width="100%"
                onBlur={handlerOnChange}
                onChange={handlerOnChange}
                onEnter={handlerOnClick}
                value={search.byForm.nombre}
                disabled={areFormFieldsDisabled}
                error={errors.nombre}
            />
            <GTIWebTextField
                name="apellido"
                labelText="Apellidos"
                placeholder="Apellido o apellidos del empleado"
                width="100%"
                onBlur={handlerOnChange}
                onChange={handlerOnChange}
                onEnter={handlerOnClick}
                value={search.byForm.apellido}
                disabled={areFormFieldsDisabled}
                error={errors.apellido}
            />
            <GTIWebDatePicker
                name="fechaNacimiento"
                labelText="Fecha de Nacimiento"
                width="100%"
                onChange={handlerOnChange}
                value={search.byForm.fechaNacimiento}
                disabled={areFormFieldsDisabled}
            />
            <GTIWebSelect
                name="estadoNacimiento"
                dataSource={states}
                displayedValue="EstadoNombre"
                selectedValue="EstadoID"
                labelText="Estado de Nacimiento:"
                color={"secondary"}
                disableDefaultReadOnly
                onChange={handlerOnChange}
                value={search.byForm.estadoNacimiento}
                disabled={areFormFieldsDisabled}
            />

            <GTIWebButton 
                name="search"
                type="submit"
                text="Buscar empleado"
                sx={styles.btnSearch}
                onClick={handlerOnClick}
                disabled={isSearchDisabled()}
            />

            <GTIWebButton 
                name="clear"
                type="submit"
                text="Limpiar búsqueda"
                sx={styles.btnClear}
                onClick={handlerOnClick}
            />
        </Stack>
    );
};

export default Search;
